import { Match, Deck, Statistics, DeckStatistics, DateRange } from '../types';

export const useStatistics = (matches: Match[], decks: Deck[]) => {
  const calculateStatistics = (filteredMatches: Match[]): Statistics => {
    const totalMatches = filteredMatches.length;
    const wins = filteredMatches.filter(match => match.result === 'win').length;
    const losses = totalMatches - wins;
    const winRate = totalMatches > 0 ? (wins / totalMatches) * 100 : 0;

    return {
      totalMatches,
      winRate,
      wins,
      losses
    };
  };

  const getDeckStatistics = (deckId: string, dateRange?: DateRange, filteredMatches?: Match[]): DeckStatistics => {
    const deck = decks.find(d => d.id === deckId);
    const matchesToUse = filteredMatches || matches;
    const deckMatches = matchesToUse.filter(match => {
      const matchDate = new Date(match.date);
      const inDateRange = !dateRange || 
        ((!dateRange.startDate || matchDate >= dateRange.startDate) &&
        (!dateRange.endDate || matchDate <= dateRange.endDate));
      
      return match.userDeckId === deckId && inDateRange;
    });

    const overallStats = calculateStatistics(deckMatches);

    const opponentStats: { [opponentDeck: string]: Statistics } = {};
    const opponentSet = new Set(deckMatches.map(match => match.opponentDeck));
    const opponents = Array.from(opponentSet);
    
    opponents.forEach(opponent => {
      const opponentMatches = deckMatches.filter(match => match.opponentDeck === opponent);
      opponentStats[opponent] = calculateStatistics(opponentMatches);
    });

    return {
      deckId,
      deckName: deck?.name || 'Unknown Deck',
      ...overallStats,
      opponentStats
    };
  };

  const getAllDeckStatistics = (dateRange?: DateRange, filteredMatches?: Match[]): DeckStatistics[] => {
    return decks.map(deck => getDeckStatistics(deck.id, dateRange, filteredMatches));
  };

  const getOverallStatistics = (dateRange?: DateRange, filteredMatches?: Match[]): Statistics => {
    const matchesToUse = filteredMatches || matches;
    const dateFilteredMatches = matchesToUse.filter(match => {
      const matchDate = new Date(match.date);
      return !dateRange || 
        ((!dateRange.startDate || matchDate >= dateRange.startDate) &&
        (!dateRange.endDate || matchDate <= dateRange.endDate));
    });

    return calculateStatistics(dateFilteredMatches);
  };

  const filterMatchesByDateRange = (dateRange: DateRange) => {
    return matches.filter(match => {
      const matchDate = new Date(match.date);
      return (!dateRange.startDate || matchDate >= dateRange.startDate) &&
             (!dateRange.endDate || matchDate <= dateRange.endDate);
    });
  };

  return {
    getDeckStatistics,
    getAllDeckStatistics,
    getOverallStatistics,
    calculateStatistics,
    filterMatchesByDateRange
  };
};